var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable } from "mobx";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
import { DocumentRetrievalTaskStatus, DocumentVectorizationStatus } from "healthex-common/document.retrieval.dtos";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getObservableT } from "../Infra/i18next";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { filterNull, willNeverHappen } from "healthex-common/utils";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { openAsyncWindow } from "./WindowHelpers";
const t = getObservableT("patients");
export class PatientConsentConfirmedViewModel extends PatientConsentViewModel {
    constructor() {
        super(...arguments);
        this.hasTrackedPageView = false;
        this.aiCountdownSecondsTimer = null;
        this.maxAiCountdownSeconds = 10;
        this.aiCountdownSeconds = this.maxAiCountdownSeconds;
        this.isCompleting = false;
    }
    run() {
        this.disposer.reaction(() => {
            var _a, _b;
            return ({
                isRecordsFound: this.isRecordsFound,
                patientId: (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientId,
                studyId: this.study !== NOT_LOADED ? (_b = this.study) === null || _b === void 0 ? void 0 : _b.id : null,
            });
        }, () => {
            var _a;
            if (!this.hasTrackedPageView && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientId)) {
                this.hasTrackedPageView = true;
                this.trackPageView();
                void this.patientConsentUIManager.deleteConsentFlowProgress();
            }
        }, { name: "trackPageView", fireImmediately: true });
        this.disposer.reaction(() => this.showAiCountdownTimer, (showAiCountdownTimer) => {
            if (showAiCountdownTimer && this.aiCountdownSeconds === this.maxAiCountdownSeconds) {
                this.startAiCountdownTimer();
            }
        }, { name: "showAiCountdownTimer", fireImmediately: true });
    }
    stop() {
        this.disposer.dispose();
        this.clearAiCountdownTimer();
    }
    startAiCountdownTimer() {
        this.clearAiCountdownTimer();
        this.aiCountdownSecondsTimer = window.setInterval(() => {
            if (this.aiCountdownSeconds === 0) {
                this.clearAiCountdownTimer();
                void this.onComplete();
                console.log(`Redirecting to ${this.aiToolName} after wating for ${this.maxAiCountdownSeconds}.`);
            }
            this.aiCountdownSeconds = Math.max(0, this.aiCountdownSeconds - 1);
        }, 1000);
    }
    clearAiCountdownTimer() {
        if (this.aiCountdownSecondsTimer !== null)
            window.clearInterval(this.aiCountdownSecondsTimer);
        this.aiCountdownSecondsTimer = null;
    }
    trackPageView() {
        var _a, _b, _c;
        heap.track("PatientConsentConfirmed", {
            patientId: (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientId,
            studyId: this.study !== NOT_LOADED ? (_b = this.study) === null || _b === void 0 ? void 0 : _b.id : null,
            batchId: (_c = this.retrieval) === null || _c === void 0 ? void 0 : _c.batchId,
            recordsFound: this.isRecordsFound,
            isAIFlow: this.isAIFlow,
            isVectorizationComplete: this.isVectorizationComplete,
        });
    }
    get zeroLocationsFound() {
        return this.patientConsentUIManager.zeroLocationsFound;
    }
    get isVectorizationComplete() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.COMPLETE;
    }
    get isRecordsFound() {
        return this.patientConsentUIManager.mayHaveAccessibleRecordsOnceReady;
    }
    get shouldHideUnifiedRecordButton() {
        if (this.isScreenshotMode) {
            return this.recordsSummary === null;
        }
        const retrieval = this.patientConsentUIManager.retrieval;
        if (!retrieval)
            return true;
        if (!retrieval.finalized) {
            return true;
        }
        if (retrieval.finalized && retrieval.pdfStatus !== PatientDataVisualizationPdfStatus.COMPLETED) {
            return true;
        }
        return false;
    }
    get title() {
        if (this.isIASAndStillWaitingForFinalization) {
            return t("We’re still working on your HealthEx Unified Record");
        }
        if (this.isIAS && this.isRecordsFound) {
            return t("Your HealthEx Unified Record is ready!");
        }
        if (this.isPreview || this.isScreenshotPreviewMode || this.isRecordsFound) {
            return this.isDataUpdate
                ? this.getCustomizedText("dataUpdateThankYouConsentedTitle")
                : this.getCustomizedText("thankYouConsentedTitle");
        }
        return t("Thank you for your time.");
    }
    get aiToolName() {
        return this.patientConsentUIManager.aiToolName;
    }
    get isIASAndStillWaitingForFinalization() {
        return this.isIAS && this.retrieval && !this.retrieval.finalized;
    }
    get supportingText() {
        var _a;
        if (this.isIASAndStillWaitingForFinalization) {
            return t("Things are taking a bit longer than expected. You can leave this page and we’ll let you know when it’s ready.");
        }
        if (this.isIAS) {
            if (this.isRecordsFound) {
                if (this.preferredContactMethod === NOT_LOADED)
                    return "";
                const contactPreference = (_a = this.preferredContactMethod) !== null && _a !== void 0 ? _a : ContactMethod.EMAIL;
                switch (contactPreference) {
                    case ContactMethod.EMAIL:
                        return t("We’ve emailed your copy to {{ email }}.", { email: this.patientEmail });
                    case ContactMethod.TEXT:
                    case ContactMethod.CALL:
                    case ContactMethod.DO_NOT_CONTACT:
                        return t("Make sure to download your copy.");
                    default:
                        return willNeverHappen(contactPreference);
                }
            }
            else if (this.hasErrorLocations) {
                return t("You've agreed to access your records, however, we couldn’t access them due to a connection issue.");
            }
            else {
                return t("You've agreed to access your records, however, no records are available right now.");
            }
        }
        if (this.isPreview || this.isScreenshotPreviewMode || this.isRecordsFound) {
            return this.isDataUpdate
                ? this.getCustomizedText("dataUpdateThankYouConsentedText")
                : this.getCustomizedText("thankYouConsentedText");
        }
        if (this.isAIFlow) {
            return this.hasErrorLocations
                ? t("You've agreed to connect with {{ aiToolName }}, however, we couldn’t access them due to a connection issue. No records were connected.", {
                    aiToolName: this.aiToolName,
                })
                : t("You agreed to connect your records to {{ aiToolName }}, though no records are actively connected at this time.", {
                    aiToolName: this.aiToolName,
                });
        }
        return t("You've agreed to share your records with {{ orgName }}, however, no records are available right now.", {
            orgName: this.organizationName,
        });
    }
    get confirmationText() {
        if (this.isPreview || this.isScreenshotPreviewMode) {
            return t("We’ll email a copy of your records to {{ email }}.", { email: this.patientEmail });
        }
        if (this.isIASAndStillWaitingForFinalization) {
            if (this.isEmailContactPreference) {
                return t("We’ll email a copy of your records to {{ email }}.", { email: this.patientEmail });
            }
            else {
                return t("We’ll text a copy of your records to {{ phone }}.", { phone: this.patientPhone });
            }
        }
        if (!this.isRecordsFound) {
            if (this.isIAS) {
                return t("If your records become available later, you can try again anytime on HealthEx.");
            }
            else if (this.isAIFlow) {
                return t("You can return to add records at any time.");
            }
            else {
                return t("If your records become available later, we may reach out again.");
            }
        }
        if (this.isEmailContactPreference) {
            return this.isAIFlow
                ? t("We’ll email you at {{ email }} when your records are ready.", { email: this.patientEmail })
                : t("We’ll email a copy of your records to {{ email }}.", { email: this.patientEmail });
        }
        else {
            return this.isAIFlow
                ? t("We’ll text you at {{ phone }} when your records are ready.", { phone: this.patientPhone })
                : t("We’ll text a copy of your records to {{ phone }}.", { phone: this.patientPhone });
        }
    }
    get confirmationAccessSettingsText() {
        return t("You can review and change access settings at any time on HealthEx.");
    }
    get isEmailContactPreference() {
        return this.preferredContactMethod === "EMAIL";
    }
    get organizationLogoUrl() {
        if (this.patientConsentUIManager.studyInfo !== NOT_LOADED && this.patientConsentUIManager.studyInfo !== null) {
            return this.patientConsentUIManager.studyInfo.addedByOrganization.logoUrl;
        }
        return null;
    }
    get errorLocations() {
        if (this.isPreview || this.isScreenshotPreviewMode)
            return null;
        const retrieval = this.patientConsentUIManager.retrieval;
        if (!retrieval)
            return null;
        const errorLocations = filterNull(retrieval.locations
            .filter((location) => retrieval.documents.some((document) => document.locationId === location.id && document.status === DocumentRetrievalTaskStatus.ERROR))
            .map((location) => location.organizationName));
        if (errorLocations.length === 0)
            return null;
        const uniqueLocations = errorLocations.sort((a, b) => a.localeCompare(b));
        return uniqueLocations;
    }
    get hasErrorLocations() {
        return !!this.errorLocations;
    }
    get recordsSummary() {
        return this.patientConsentUIManager.recordsSummary;
    }
    get aiConfirmButtonText() {
        if (!this.canRedirectExternally) {
            return t("Go to HealthEx");
        }
        return this.isDataUpdate
            ? t("Go to {{ aiToolName }}", { aiToolName: this.aiToolName })
            : t("Finish and return to {{ aiToolName }}", { aiToolName: this.aiToolName });
    }
    get timeToConnectInformation() {
        return this.isAIFlow && this.isRecordsFound ? this.getCustomizedText("timeToConnectInformation") || null : null;
    }
    get errorBannerText() {
        return this.isAIFlow
            ? "Due to connection issues, we couldn't access all records from: <b>{{ providers }}</b>. Records from your other providers were successfully connected."
            : "Due to connection issues, we couldn't access records from: <b>{{ providers }}</b>. Records from your other connected providers were successfully added to your HealthEx Unified Record.";
    }
    get showAiCountdownTimer() {
        if (!this.canRedirectExternally)
            return false;
        return this.isAIFlow && this.isRecordsFound && !this.hasErrorLocations;
    }
    async onComplete() {
        if (this.isPreview)
            return;
        if (this.isCompleting)
            return;
        this.isCompleting = true;
        this.clearAiCountdownTimer();
        if (this.isOAuthFlow) {
            await this.patientConsentUIManager.completeOAuthAfterPDDE();
        }
        else if (this.hasHostedRedirectUrl) {
            await this.patientConsentUIManager.handleRedirectForHostedFlow("success");
        }
        else {
            this.patientConsentUIManager.done();
        }
    }
    async showUnifiedRecord(type = "pdf") {
        void openAsyncWindow(async () => {
            var _a, _b;
            const url = type === "raw"
                ? (_a = (await this.patientConsentUIManager.getDownloadRawDocumentsLink())) === null || _a === void 0 ? void 0 : _a.downloadLink
                : (_b = (await this.patientConsentUIManager.getDownloadPdfLink())) === null || _b === void 0 ? void 0 : _b.downloadLink;
            if (!url) {
                console.warn(`No URL available`);
                return null;
            }
            return url;
        });
    }
}
__decorate([
    observable
], PatientConsentConfirmedViewModel.prototype, "aiCountdownSeconds", void 0);
__decorate([
    observable
], PatientConsentConfirmedViewModel.prototype, "isCompleting", void 0);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "zeroLocationsFound", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isVectorizationComplete", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isRecordsFound", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "shouldHideUnifiedRecordButton", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "title", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "aiToolName", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isIASAndStillWaitingForFinalization", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "supportingText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "confirmationText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "confirmationAccessSettingsText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isEmailContactPreference", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "organizationLogoUrl", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "errorLocations", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "hasErrorLocations", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "recordsSummary", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "aiConfirmButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "timeToConnectInformation", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "errorBannerText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "showAiCountdownTimer", null);
